import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Autocomplete, Checkbox, colors, Typography } from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { objType } from "data/globalData";
import { inputTypes } from "data/globalData";
import { allOption } from "components/ReplenishmentDashboard/data";
import { useCallback } from "react";

const SelectInputField = ({ data, setData, column, width, options, sx, onChange, disabled, required, disabledOptionFun }) => {
	const sxData = sx ? sx : {};
	let { type, list, getOptionLabel } = options;

	if (type === objType) list = Object.keys(list)?.map((key) => list[key]);
	let optionsList = Array.isArray(list) ? [...list] : [];

	const multiple = column.input_type === inputTypes.MULTIPLE_SELECT.key;
	const selectedValue = data?.[column.key] ?? (multiple ? [] : null);

	// Normalize options
	if (typeof getOptionLabel === "function") {
		optionsList = list?.map((row) => {
			const { name, key } = getOptionLabel(row); // Get the name and key
			return {
				name, // { name } from getOptionLabel
				key, // { key } from getOptionLabel
				label: name,
			};
		});
	} else {
		optionsList = optionsList.map((row) => ({
			...row,
			label: row?.name ?? row?.label,
		}));
	}

	const allOptionKeys = optionsList.map((o) => o.key); // Get all the keys

	// Select All item
	const SELECT_ALL = { key: "__select_all__", label: "Select All" };
	const withSelectAll = multiple ? [SELECT_ALL, ...optionsList] : optionsList;

	const isAllSelected = multiple && selectedValue?.length === optionsList.length;
	const hasValues = Array.isArray(data[column.key]) && data[column.key].length > 0;

	// Change handler
	const handleChange = (event, value) => {
		// If not multiple, handle normal single-select case
		if (!multiple) {
			const selected = value?.key ?? null;
			if (typeof onChange === "function") {
				onChange({ target: { value: selected } });
			} else {
				setData({ ...data, [column.key]: selected });
			}
			return;
		}

		// Handle multiple select case
		const hasSelectAll = value[value.length - 1]?.key === SELECT_ALL.key;
		const selectedKeys = value
			.filter((v) => v.key !== SELECT_ALL.key) // Exclude the "Select All"
			.map((v) => v.key); // Map selected values to their keys

		const finalKeys = hasSelectAll
			? isAllSelected
				? []
				: allOptionKeys // Select all or deselect all
			: selectedKeys; // Regular selection (no "Select All")

		// Update the state
		if (typeof onChange === "function") {
			onChange({ target: { value: finalKeys } });
		} else {
			setData({ ...data, [column.key]: finalKeys });
		}
	};

	const memoizedDisabledFun = useCallback(
		(column, data, option) => {
			return typeof disabledOptionFun === "function" ? disabledOptionFun(column, data, option) : false;
		},
		[data]
	);

	return (
		<Stack sx={{ width }} spacing={"3px"} height={"70px"}>
			<InputLabel sx={{ overflow: "hidden", color: grey[600] }}>
				{column?.name}
				{required && <span style={{ color: "red" }}>*</span>}
			</InputLabel>
			<Autocomplete
				disableCloseOnSelect={multiple}
				disabled={disabled}
				fullWidth
				size="small"
				multiple={multiple}
				options={withSelectAll}
				getOptionLabel={(option) => option.label}
				getOptionDisabled={(option) => option.key !== SELECT_ALL.key && memoizedDisabledFun(column, data, option)}
				value={
					multiple
						? optionsList.filter((opt) => selectedValue?.includes(opt.key))
						: optionsList.find((opt) => opt.key === selectedValue) ?? null
				}
				isOptionEqualToValue={(opt, val) => opt.key === val.key}
				onChange={handleChange}
				renderTags={(value) => (
					<span
						style={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{value.map((opt) => opt.name).join(", ")}
					</span>
				)}
				// popupIcon={<></>} //  remove dropdown icon for tighter layout
				sx={{
					"& .MuiAutocomplete-inputRoot": {
						display: "flex",
						alignItems: "center",
						flexWrap: "nowrap",
						height: "40px",
						overflow: "hidden",
						width: "100%", // Ensure input takes the remaining width
					},
					"& .MuiAutocomplete-tag": {
						flex: hasValues ? "1 1 100%" : "0 0 0%", // Tag should take 60% width when there are selected values
						width: hasValues ? "100%" : "0%",
						overflow: "hidden",
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
						marginRight: "4px",
						transition: "all 0.3s ease",
						display: hasValues ? "flex" : "none", // Only show tags when there are values
					},
					"& .MuiAutocomplete-input": {
						flex: hasValues ? "0 0 0%" : "1 1 100%", // When tags are present, hide input; else take full width
						minWidth: 0,
						width: hasValues ? "1px" : "100%",
						pointerEvents: hasValues ? "none" : "auto",
						transition: "all 0.3s ease",
						display: hasValues ? "none" : "block", // Only show tags when there are values
						padding: 0,
					},
					// "& .MuiAutocomplete-clearIndicator": {
					// 	display: "none",
					// },
					// On hover or focus — reveal input and resize tags
					"&.Mui-focused .MuiAutocomplete-input, &:hover .MuiAutocomplete-input": hasValues
						? {
								flex: "0 0 40%",
								width: "auto",
								pointerEvents: "auto",
								display: "block",
						  }
						: {},
					"&.Mui-focused .MuiAutocomplete-tag, &:hover .MuiAutocomplete-tag": hasValues
						? {
								flex: "0 0 60%",
								maxWidth: "60%",
								display: "flex",
						  }
						: {},
				}}
				renderOption={(props, option, { selected }) => {
					const isSelectAll = option.key === SELECT_ALL.key;
					const isChecked = isSelectAll ? isAllSelected : selected;
					const isIndeterminate = isSelectAll && selectedValue.length > 0 && selectedValue.length < optionsList.length;

					return (
						<li {...props}>
							{multiple && (
								<Checkbox
									checked={isChecked}
									indeterminate={isIndeterminate}
									sx={{ margin: 0, padding: "2px", mr: 1 }}
									disableRipple
								/>
							)}
							<Typography color={isSelectAll ? green[600] : "inherit"}>{option.label}</Typography>
						</li>
					);
				}}
				renderInput={(params) => <TextField {...params} placeholder={`Search ${column?.name}`} variant="outlined" />}
			/>
		</Stack>
	);
};

export default SelectInputField;

// import TextField from "@mui/material/TextField";
// import InputLabel from "@mui/material/InputLabel";
// import Stack from "@mui/material/Stack";
// import Select from "@mui/material/Select";
// import MenuItem from "@mui/material/MenuItem";
// import { Checkbox, colors } from "@mui/material";
// import { green, grey } from "@mui/material/colors";
// import { objType } from "data/globalData";
// import { inputTypes } from "data/globalData";
// import { allOption } from "components/ReplenishmentDashboard/data";
// import { useCallback } from "react";

// const SelectInputField = ({ data, setData, column, width, options, sx, onChange, disabled, required, disabledOptionFun }) => {
// 	const sxData = sx ? sx : {};
// 	let { type, list, getOptionLabel } = options;
// 	const classes = {
// 		selectedAll: {
// 			"&.Mui-checked": {
// 				color: green[600],
// 			},
// 		},
// 	};

// 	let onChangeFun = typeof onChange === "function" ? onChange : (e) => setData({ ...data, [column.key]: e.target.value });
// 	// options
// 	if (type === objType) list = Object.keys(list)?.map((key) => list[key]);
// 	let optionsList = Array.isArray(list) ? [...list] : [];
// 	if (typeof getOptionLabel === "function") optionsList = list?.map((row) => getOptionLabel(row));

// 	// multiple select
// 	const multiple = column.input_type === inputTypes.MULTIPLE_SELECT.key ? true : false;
// 	const isAllSelected = optionsList.length > 0 && data?.[column.key]?.length === optionsList.length;

// 	const mainOnChange = onChangeFun;
// 	if (multiple)
// 		onChangeFun = (event) => {
// 			const value = event.target.value;
// 			if (value[value.length - 1] === allOption) {
// 				event.target.value = data?.[column.key]?.length === optionsList.length ? [] : optionsList?.map((row) => row.key);
// 				console.log("anfvbxnmfvxncsgfnyx", event.target.value);
// 			} else event.target.value = value;
// 			mainOnChange.call(this, event);
// 		};

// 	const memoizedDisabledFun = useCallback(
// 		(column, data, option) => {
// 			return typeof disabledOptionFun === "function" ? disabledOptionFun(column, data, option) : false;
// 		},
// 		[data] // Recalculate when `data` changes
// 	);

// 	return (
// 		<Stack sx={{ width }} spacing={"3px"} height={"70px"}>
// 			<InputLabel sx={{ overflow: "hidden", color: grey[600] }}>
// 				{column?.name}
// 				{required && <span style={{ color: "red" }}>*</span>}
// 			</InputLabel>
// 			<Select
// 				size="small"
// 				fullWidth
// 				displayEmpty
// 				multiple={multiple}
// 				variant="outlined"
// 				required={required}
// 				placeholder={`Enter ${column?.name}`}
// 				MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
// 				value={data?.[column.key] ? data[column.key] : multiple ? [] : null}
// 				renderValue={(selected) => {
// 					if (multiple) {
// 						if (!selected?.length) return <>{inputTypes.SELECT.key}</>;
// 						else return selected?.join(", ");
// 					} else return selected;
// 				}}
// 				disabled={disabled}
// 				onChange={onChangeFun}>
// 				{multiple ? (
// 					<MenuItem
// 						value={allOption}
// 						classes={{
// 							root: isAllSelected ? classes.selectedAll : "",
// 						}}>
// 						<Checkbox
// 							checked={isAllSelected}
// 							indeterminate={data?.[column.key]?.length > 0 && data?.[column.key]?.length < optionsList.length}
// 							size="small"
// 							disableRipple
// 							sx={{ margin: 0, padding: "2px", mr: 1 }}
// 						/>{" "}
// 						Select All
// 					</MenuItem>
// 				) : (
// 					<MenuItem value={inputTypes.SELECT.key} disabled>
// 						<span style={{ color: grey[400] }}>Select</span>
// 					</MenuItem>
// 				)}
// 				{multiple
// 					? optionsList?.map((row) => {
// 							return (
// 								<MenuItem
// 									key={row?.key}
// 									value={row?.key}
// 									// disabled={disabledDropdowns?.includes(row?.key) && !data?.[column.key]?.includes(row?.key)}
// 									disabled={memoizedDisabledFun(column, data, row)}>
// 									<Checkbox
// 										checked={data?.[column.key]?.includes(row?.key)}
// 										size="small"
// 										disableRipple
// 										sx={{ margin: 0, padding: "2px", mr: 1 }}
// 									/>
// 									{row?.name}
// 								</MenuItem>
// 							);
// 					  })
// 					: optionsList?.map((row) => {
// 							return (
// 								<MenuItem
// 									key={row?.key}
// 									value={row?.key}
// 									// disabled={disabledDropdowns?.includes(row?.key)}
// 									disabled={memoizedDisabledFun(column, data, row)}>
// 									{row?.name}
// 								</MenuItem>
// 							);
// 					  })}
// 			</Select>
// 		</Stack>
// 	);
// };

// export default SelectInputField;
